import React, { ReactNode, useEffect, useState } from "react";
import { X, Plus, Settings, Eye } from "lucide-react";
import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import CompanyLogo from "@/assets/company-logo.png";
import {
  useLocation,
  useSearchParams,
  useParams,
  useNavigate,
} from "react-router-dom";
import CampaignDashboardVisualView from "./components/campaign-detail-visual-view";
import useCampaignDetailStore from "./store/useCampaignDetailStore";
import Spinner from "@/components/ui/Spinner";
import CampaignDetailAdCategoryDetails from "./components/ad-category-details";
import useGlobalStore from "@/store/globalStore";
import AdBuilder from "./components/ad-builder";
import CampaignDetailListView from "./components/campaign-detail-list-view";

interface TeamMember {
  initials: string;
  color: string;
}

interface StatusItem {
  label: string;
  value: number;
  color: string;
  icon: string;
  showCheckmark?: boolean;
}

const CampaignDetailWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [isLoading, setIsLoading] = useState(false);

  const setError = useGlobalStore((state) => state.setError);

  const location = useLocation();

  const { campaignDetail, getCampaignDetailData } = useCampaignDetailStore();

  const [isExpanded, setIsExpanded] = useState(false);
  const [activeView, setActiveView] = useState<"visual" | "list" | "blocking">(
    "visual"
  );
  const [activeFilter, setActiveFilter] = useState("All Flights");

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await getCampaignDetailData(campaignId);
    } catch (error) {
      setError(error.message ?? "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch campaign data on mount
  useEffect(() => {
    if (campaignId) {
      fetchData();
    }
  }, [campaignId]);

  // Set view from URL params
  useEffect(() => {
    const view = searchParams.get("view");
    if (view) {
      setActiveView(view as "visual" | "list" | "blocking");
    }
  }, []);

  // Generate team member initials and colors from actual team data
  const teamMembers: TeamMember[] =
    campaignDetail?.metadata.teams.map((team, idx) => ({
      initials: team.name
        .split(" ")
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase(),
      color: idx % 2 === 0 ? "bg-pink-500" : "bg-blue-500",
    })) || [];

  // Calculate status items based on actual data
  const statusItems: StatusItem[] = [
    {
      label: "Briefed",
      value: 0,
      color: "bg-yellow-300",
      icon: "📄",
      showCheckmark: true,
    },
    {
      label: "In Progress",
      value: 0,
      color: "bg-orange-400",
      icon: "💡",
      showCheckmark: true,
    },
    { label: "In Review", value: 100, color: "bg-red-300", icon: "🎬" },
    { label: "Approved", value: 45, color: "bg-lime-300", icon: "✓" },
    { label: "Trafficking", value: 0, color: "bg-blue-200", icon: "⚙️" },
    { label: "Live", value: 0, color: "bg-cyan-200", icon: "❄️" },
  ];

  // Extract unique ad types from campaign data
  const adTypes =
    campaignDetail?.nested_by_flight
      .flatMap((flight) =>
        flight.flight_rows.flatMap((row) =>
          row.flight_ad_categories.flatMap((cat) =>
            cat.ad_rows.map((ad) => ad.ad_type)
          )
        )
      )
      .filter((value, index, self) => self.indexOf(value) === index) || [];

  // Calculate total assets
  const totalAssets =
    campaignDetail?.nested_by_language_objective.reduce(
      (sum, lang) =>
        sum +
        lang.objectives.reduce(
          (objSum, obj) => objSum + obj.metrics.total_assets,
          0
        ),
      0
    ) || 0;

  // Get languages
  const languages =
    campaignDetail?.metadata.languages
      .map((lang) => lang.name.slice(0, 2).toUpperCase())
      .join(", ") || "";

  // Format date range
  const formatDateRange = () => {
    if (!campaignDetail?.nested_by_flight?.length || !campaignDetail.nested_by_flight[0]) return "N/A";
    const flights = campaignDetail.nested_by_flight;
    const startDate = new Date(flights[0]?.start_date).toLocaleDateString();
    const endDate = new Date(
      flights[flights.length - 1]?.end_date
    ).toLocaleDateString();
    return `${startDate} – ${endDate}`;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (!campaignDetail) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">No campaign data found</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Main Content */}
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Campaign Dashboard</h1>
          <Button
            type="secondary"
            leadingIcon={<Settings size={20} />}
            onClick={() => {
              navigate(`/campaign?id=${campaignId}`);
            }}
          >
            Campaign Settings
          </Button>
        </div>

        {/* Campaign Card */}
        <div className="mb-8 mt-14">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div>
                <img src={CompanyLogo} className="rounded-lg w-20 h-20" />
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">
                  {campaignDetail.metadata.teams?.filter(
                    (e) => e.team_type == "client"
                  )?.[0]?.name || "Agency"}
                </div>
                <h2 className="text-2xl font-medium">
                  {campaignDetail.metadata.campaign.name}
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-full overflow-hidden border border-[#EA33C0]">
                <button
                  onClick={() => {
                    searchParams.set("view", "visual");
                    setSearchParams(searchParams);
                    setActiveView("visual");
                  }}
                  className={`px-8 py-2 font-medium text-sm transition-colors border-r border-[#EA33C0] ${
                    activeView === "visual"
                      ? "bg-[#EA33C0] text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Visual View
                </button>
                <button
                  onClick={() => {
                    searchParams.set("view", "list");
                    setSearchParams(searchParams);
                    setActiveView("list");
                  }}
                  className={`px-8 py-2 font-medium text-sm transition-colors border-r border-[#EA33C0] ${
                    activeView === "list"
                      ? "bg-[#EA33C0] text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  List View
                </button>
                <button
                  onClick={() => {
                    searchParams.set("view", "blocking");
                    setSearchParams(searchParams);
                    setActiveView("blocking");
                  }}
                  className={`px-8 py-2 font-medium text-sm transition-colors ${
                    activeView === "blocking"
                      ? "bg-[#EA33C0] text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Blocking Chart
                </button>
              </div>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="ml-4 text-pink-500 p-2 rounded"
              >
                {isExpanded ? <X size={28} /> : <Plus size={28} />}
              </button>
            </div>
          </div>

          {isExpanded && (
            <>
              {/* Project Details */}
              <div className="grid grid-cols-2 gap-8 mb-6 text-sm">
                <div>
                  <div className="grid grid-cols-4 gap-6 mb-6">
                    <div>
                      <div className="font-semibold mb-1">Project Number</div>
                      <div>{campaignDetail.metadata.campaign.project_code}</div>
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Total Budget</div>
                      <div>
                        {campaignDetail?.metadata?.campaign?.budget != null
                          ? `$${campaignDetail.metadata.campaign.budget.toLocaleString()}`
                          : "N/A"}
                      </div>
                    </div>
                    <div className="mb-6 col-span-2">
                      <div className="font-semibold mb-1">In Market Date</div>
                      <div>{formatDateRange()}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-6 mb-6">
                    <div>
                      <div className="font-semibold mb-1">Total Flights</div>
                      <div>{campaignDetail.nested_by_flight.length}</div>
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Languages</div>
                      <div>{languages}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-sm font-semibold mb-2">Team</div>
                      <div className="flex gap-2">
                        {teamMembers.map((member, idx) => (
                          <div
                            key={idx}
                            className={`w-8 h-8 rounded-full ${member.color} text-white flex items-center justify-center font-semibold text-xs`}
                          >
                            {member.initials}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold mb-2">Ad Types</div>
                    <div className="flex flex-wrap gap-2">
                      {adTypes.map((type, idx) => (
                        <span
                          key={idx}
                          className="px-4 py-1.5 bg-white border border-gray-300 rounded-full text-sm"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Status Tracker */}
                <div>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1 mr-4 min-w-0">
                      <div className="text-lg font-semibold mb-3">
                        Status Tracker
                      </div>
                      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hidden">
                        {[
                          "All Flights",
                          ...campaignDetail.nested_by_flight.map(
                            (flight, idx) => `Flight ${flight.flight_number}`
                          ),
                        ].map((filter) => (
                          <button
                            key={filter}
                            onClick={() => setActiveFilter(filter)}
                            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                              activeFilter === filter
                                ? "bg-gray-700 text-white"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                          >
                            {filter}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-4xl font-bold">{totalAssets}</div>
                      <div className="text-sm text-gray-600">Total Assets</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {statusItems.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-24 text-sm font-medium">
                          {item.label}
                        </div>
                        <div className="flex-1 relative">
                          <div
                            className={`h-8 flex items-center overflow-hidden`}
                          >
                            <div
                              className={`absolute inset-0 ${item.color} opacity-20 rounded-full`}
                            ></div>
                            {item.value > 0 && (
                              <div
                                className={`absolute left-0 top-0 h-full ${item.color} rounded-full`}
                                style={{ width: `${item.value}%` }}
                              ></div>
                            )}

                            <div className="relative z-10 flex items-center justify-between w-full px-3">
                              <span className="text-lg">{item.icon}</span>
                              {item.value > 0 && (
                                <span className="font-semibold text-sm">
                                  {item.value}
                                </span>
                              )}
                              {item.showCheckmark && (
                                <span className="text-lg">✓</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <hr className="mt-2 border-[#EA33C0] mb-8" />

        {activeView === "visual" &&
        location.pathname === `/campaign/${campaignId}` ? (
          <CampaignDashboardVisualView />
        ) : null}

        {activeView === "visual" &&
        location.pathname === `/campaign/${campaignId}/ad-category` ? (
          <CampaignDetailAdCategoryDetails />
        ) : null}

        {activeView === "visual" &&
        location.pathname === `/campaign/${campaignId}/ad-builder` ? (
          <AdBuilder />
        ) : null}

        {activeView === "list" &&
        location.pathname === `/campaign/${campaignId}` ? (
          <CampaignDetailListView />
        ) : null}
      </div>
    </Layout>
  );
};

export default CampaignDetailWrapper;
